import type { InspectionFormState, InspectionFormAction } from "../useInspectionForm";
import { useCtaCatalogs } from "@/hooks";
import type { CtaFieldLimitApi } from "@/lib/api";
import FieldLimitHint, { checkField, inputStateCls, limitAttrs } from "./FieldLimitHint";

interface Props {
  state: InspectionFormState;
  dispatch: React.Dispatch<InspectionFormAction>;
}

const labelCls = "text-xs font-medium text-foreground-500 mb-1 block";
const inputCls = "w-full bg-white border border-background-200/70 rounded-lg px-3 py-2 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors";

const num = (raw: string): number | null => (raw === "" ? null : parseFloat(raw));

interface MeasureFieldProps {
  label: string;
  value: number | null | undefined;
  limit: CtaFieldLimitApi | undefined;
  onChange: (raw: string) => void;
  placeholder?: string;
  help?: string;
  extraCls?: string;
}

function MeasureField({ label, value, limit, onChange, placeholder, help, extraCls = "" }: MeasureFieldProps) {
  const check = checkField(limit, value);
  return (
    <div>
      <label className={labelCls}>{label}</label>
      <input
        type="number"
        inputMode="decimal"
        {...limitAttrs(limit)}
        placeholder={placeholder}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={check?.level === "error" || undefined}
        className={`${inputCls} ${extraCls} ${inputStateCls(check)}`}
      />
      {check ? <FieldLimitHint check={check} /> : help && <p className="text-[10px] text-foreground-400 mt-0.5">{help}</p>}
    </div>
  );
}

/** Aviso quando CAP e DAP, informados juntos, nao descrevem o mesmo tronco (DAP = CAP / pi). */
function capDapMismatch(cap: number | null | undefined, dap: number | null | undefined, tolerance: number): string | null {
  if (!cap || !dap) return null;
  const expected = cap / Math.PI;
  if (Math.abs(expected - dap) / dap <= tolerance) return null;
  return `CAP ${cap} cm corresponde a DAP ${expected.toFixed(1)} cm, mas o DAP informado é ${dap} cm. Confira qual dos dois foi medido.`;
}

export default function StepDendrometria({ state, dispatch }: Props) {
  const m = state.measurement;
  const catalogs = useCtaCatalogs();
  const limits = catalogs?.field_limits?.fields ?? {};
  const tolerance = catalogs?.field_limits?.cap_dap_tolerance ?? 0.05;

  const handleCapChange = (value: string) => {
    const capCm = parseFloat(value);
    if (!isNaN(capCm) && capCm > 0) {
      dispatch({ type: "CALCULATE_DAP", capCm });
    } else {
      dispatch({ type: "SET_MEASUREMENT", field: "capCm", value: num(value) });
    }
  };

  const mainMismatch = capDapMismatch(m.capCm, m.dapCm, tolerance);

  return (
    <div className="space-y-5">
      {/* Main measurements */}
      <div className="bg-white rounded-xl border border-background-200/70 p-4">
        <h4 className="text-sm font-semibold text-foreground-800 flex items-center gap-2 mb-1">
          <i className="ri-ruler-line text-accent-500"></i>
          Medições Principais (fuste principal)
        </h4>
        <p className="text-xs text-foreground-500 mb-4">
          Deixe em branco o que não foi medido. Valores fora do limite físico não são salvos; valores fora do usual geram aviso.
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <MeasureField
            label="CAP (cm)"
            value={m.capCm}
            limit={limits.cap_cm}
            onChange={handleCapChange}
            placeholder="Ex: 141.4"
            help="Circunferência à Altura do Peito"
          />
          <MeasureField
            label="DAP (cm)"
            value={m.dapCm}
            limit={limits.dap_cm}
            onChange={(v) => dispatch({ type: "SET_MEASUREMENT", field: "dapCm", value: num(v) })}
            placeholder="Calculado"
            help={m.measurementSource === "CALCULATED_FROM_CAP" ? "DAP = CAP / π" : "Diâmetro à Altura do Peito"}
            extraCls={m.measurementSource === "CALCULATED_FROM_CAP" ? "bg-primary-50/30" : ""}
          />
          <MeasureField
            label="Altura (m)"
            value={m.heightM}
            limit={limits.height_m}
            onChange={(v) => dispatch({ type: "SET_MEASUREMENT", field: "heightM", value: num(v) })}
            placeholder="Ex: 12.5"
          />
          <MeasureField
            label="Diâmetro copa (m)"
            value={m.crownDiameterM}
            limit={limits.crown_diameter_m}
            onChange={(v) => dispatch({ type: "SET_MEASUREMENT", field: "crownDiameterM", value: num(v) })}
            placeholder="Ex: 8.2"
          />
          <MeasureField
            label="Inclinação (graus)"
            value={m.inclinationDeg}
            limit={limits.inclination_deg}
            onChange={(v) => dispatch({ type: "SET_MEASUREMENT", field: "inclinationDeg", value: num(v) })}
            placeholder="0"
          />
        </div>
        {mainMismatch && <FieldLimitHint check={{ level: "warn", message: mainMismatch }} />}
      </div>

      {/* Multi-stem */}
      <div className="bg-white rounded-xl border border-background-200/70 p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-foreground-800 flex items-center gap-2">
            <i className="ri-git-branch-line text-primary-500"></i>
            Fustes Adicionais
          </h4>
          <button
            type="button"
            onClick={() =>
              dispatch({
                type: "ADD_STEM",
                stem: { sequence: state.stems.length + 2 },
              })
            }
            className="text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
          >
            <i className="ri-add-line"></i> Adicionar fuste
          </button>
        </div>

        {state.stems.length === 0 ? (
          <p className="text-xs text-foreground-400">Nenhum fuste adicional registrado. Use o botão acima para árvores multi-fuste.</p>
        ) : (
          <div className="space-y-3">
            {state.stems.map((stem, idx) => {
              const mismatch = capDapMismatch(stem.capCm, stem.dapCm, tolerance);
              return (
                <div key={idx} className="p-3 bg-background-50 rounded-lg border border-background-200/50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-foreground-600">Fuste #{stem.sequence}</span>
                    <button
                      type="button"
                      onClick={() => dispatch({ type: "REMOVE_STEM", index: idx })}
                      className="text-xs text-danger-500 hover:text-danger-600"
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <MeasureField
                      label="CAP (cm)"
                      value={stem.capCm}
                      limit={limits.cap_cm}
                      onChange={(v) => dispatch({ type: "UPDATE_STEM", index: idx, field: "capCm", value: num(v) })}
                    />
                    <MeasureField
                      label="DAP (cm)"
                      value={stem.dapCm}
                      limit={limits.dap_cm}
                      onChange={(v) => dispatch({ type: "UPDATE_STEM", index: idx, field: "dapCm", value: num(v) })}
                    />
                    <MeasureField
                      label="Altura (m)"
                      value={stem.heightM}
                      limit={limits.height_m}
                      onChange={(v) => dispatch({ type: "UPDATE_STEM", index: idx, field: "heightM", value: num(v) })}
                    />
                  </div>
                  {mismatch && <FieldLimitHint check={{ level: "warn", message: mismatch }} />}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
